import { useState, useId } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { FAQS } from "@/data/faqs";

// FAQ accordion + FAQPage JSON-LD schema injection.
export default function Faq({ slug }) {
    const items = FAQS[slug] || [];
    const [open, setOpen] = useState(-1);
    const baseId = useId();

    if (items.length === 0) return null;

    const schemaScript = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: items.map((it) => ({
            "@type": "Question",
            name: it.q,
            acceptedAnswer: { "@type": "Answer", text: it.a },
        })),
    };

    return (
        <section className="mt-12 pt-10 border-t border-border" data-testid="faq-section" aria-labelledby={`${baseId}-title`}>
            <div className="text-[11px] uppercase tracking-[0.2em] text-accent mb-2">
                Frequently asked
            </div>
            <h2 id={`${baseId}-title`} className="font-serif text-3xl sm:text-4xl text-primary mb-6 leading-tight">
                Common questions
            </h2>
            <div className="divide-y divide-border border-y border-border">
                {items.map((it, i) => {
                    const isOpen = open === i;
                    const panelId = `${baseId}-panel-${i}`;
                    const btnId = `${baseId}-btn-${i}`;
                    return (
                        <div key={i}>
                            <button
                                id={btnId}
                                onClick={() => setOpen(isOpen ? -1 : i)}
                                data-testid={`faq-item-${i}`}
                                className="w-full text-left py-5 group min-h-tap"
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <h3 className="font-serif text-xl text-primary leading-snug pr-4">
                                        {it.q}
                                    </h3>
                                    <ChevronDown
                                        className={cn(
                                            "w-5 h-5 mt-1 text-foreground/60 shrink-0 transition-transform duration-300",
                                            isOpen && "rotate-180 text-primary",
                                        )}
                                        strokeWidth={1.5}
                                        aria-hidden="true"
                                    />
                                </div>
                            </button>
                            <div
                                id={panelId}
                                role="region"
                                aria-labelledby={btnId}
                                hidden={!isOpen}
                                className={cn("pb-5", !isOpen && "hidden")}
                            >
                                <p className="text-[15px] leading-[1.75] text-foreground/80 max-w-3xl">
                                    {it.a}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaScript) }}
            />
        </section>
    );
}
